import { isCheckBasicCampaignDiscountActive } from 'core/service/basic-plan/basic-plan'
import _ from 'lodash'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import VALUES from 'core/data-model/constants/values'
import { getUserAgentType } from 'identity/selectors'
import { getInsuredOccupationClass } from 'quick-quote/insured-information/selectors'
import {
  editSumAssured,
  editBasicPremiumState,
  UPDATE_SUM_ASSURED$,
} from 'quick-quote/product-common/coverage-plan/actions'
import { getSumAssured } from 'quick-quote/product-common/coverage-plan/selectors'
import { getDisplayProducts, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'

import {
  SELECT_PRODUCT_LIFE_READY,
  selectBasicPlan,
  SELECT_PRODUCT_CANCERX5,
  SELECT_PRODUCT_KOOMVER_COVER,
  SELECT_PRODUCT_KOOMVER_COMPENSATE,
  SELECT_PRODUCT_LIFE_TREASURE,
} from './actions'

/**
 * createProductLifeReadyFunc
 * @param {ReturnType<import('core/service-wrapper/legacy/handler/product-common-coverage').createProductCommonCoverageFunc>} productCommonCoverageFunc
 */
export function createProductLifeReadyFunc(productCommonCoverageFunc) {
  const _isLifeReadyMultiPlanGroup = (productCode) =>
    _.includes(VALUES.LIFE_READY_LIST_PRODUCT_MULTI_PLANCODE, productCode)
  const _isLifeReady18Group = (productCode) => _.includes(VALUES.LIFE_READY_18_GROUP, productCode)
  const _isLifeReady18CampaignGroup = (productCode) => _.includes(VALUES.LIFE_READY_18_CAMPAIGN_GROUP, productCode)
  const _isLifeReady99Group = (productCode) => _.includes(VALUES.LIFE_READY_99_GROUP, productCode)

  const _isRokeRaiSoShieldMultiPlanGroup = (productCode) =>
    _.includes(VALUES.ROKE_RAI_SO_SHIELD_LIST_PRODUCT_MULTI_PLANCODE, productCode)
  const _isRokeRaiSoShield18Group = (productCode) => _.includes(VALUES.ROKE_RAI_SO_SHIELD_18_GROUP, productCode)
  const _isRokeRaiSoShield99Group = (productCode) => _.includes(VALUES.ROKE_RAI_SO_SHIELD_99_GROUP, productCode)

  const _isCIComboMultiPlanGroup = (productCode) => _.includes(VALUES.CI_COMBO_LIST_PRODUCT_MULTI_PLANCODE, productCode)
  const _isCICombo18Group = (productCode) => _.includes(VALUES.CI_COMBO_18_GROUP, productCode)
  const _isCICombo99Group = (productCode) => _.includes(VALUES.CI_COMBO_99_GROUP, productCode)

  const _isHealthComboMultiPlanGroup = (productCode) =>
    _.includes(VALUES.HEALTH_COMBO_LIST_PRODUCT_MULTI_PLANCODE, productCode)
  const _isHealthCombo18Group = (productCode) => _.includes(VALUES.HEALTH_COMBO_18_GROUP, productCode)
  const _isHealthCombo99Group = (productCode) => _.includes(VALUES.HEALTH_COMBO_99_GROUP, productCode)

  const _getLifeReadyProductGroupCode = (productCode) => {
    if (_isLifeReady18Group(productCode)) {
      return VALUES.LIFE_READY_18_GROUP
    } else if (_isLifeReady18CampaignGroup(productCode)) {
      return VALUES.LIFE_READY_18_CAMPAIGN_GROUP
    } else if (_isLifeReady99Group(productCode)) {
      return VALUES.LIFE_READY_99_GROUP
    } else if (_isRokeRaiSoShield18Group(productCode)) {
      return VALUES.ROKE_RAI_SO_SHIELD_18_GROUP
    } else if (_isRokeRaiSoShield99Group(productCode)) {
      return VALUES.ROKE_RAI_SO_SHIELD_99_GROUP
    } else if (_isCICombo18Group(productCode)) {
      return VALUES.CI_COMBO_18_GROUP
    } else if (_isCICombo99Group(productCode)) {
      return VALUES.CI_COMBO_99_GROUP
    } else if (_isHealthCombo18Group(productCode)) {
      return VALUES.HEALTH_COMBO_18_GROUP
    } else if (_isHealthCombo99Group(productCode)) {
      return VALUES.HEALTH_COMBO_99_GROUP
    }
  }

  const _isValidSumAssured = (entrySumAssured, sumAssured) => {
    const { minimum, maximum } = entrySumAssured
    return !(sumAssured < minimum || sumAssured > maximum)
  }

  const getAdjustedProductByCampaign = ({
    productSelected,
    displayProducts,
    insuredOccupationClass,
    userAgentType,
  }) => {
    const switchToBasicPlanCode = _.get(productSelected.campaignDiscount, 'switchToBasicPlanCode')
    const isBasicCampaignDiscountActive = isCheckBasicCampaignDiscountActive({
      basicPlan: productSelected,
      occupationFactor: insuredOccupationClass,
      userAgentType,
    })

    if (switchToBasicPlanCode && isBasicCampaignDiscountActive) {
      const payloadProductSwitch = _.find(displayProducts, { code: switchToBasicPlanCode })
      if (payloadProductSwitch) {
        return payloadProductSwitch
      } else {
        return productSelected
      }
    } else {
      return productSelected
    }
  }

  const switchProductBySumAssured = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (
      !_isLifeReadyMultiPlanGroup(productCode) &&
      !_isRokeRaiSoShieldMultiPlanGroup(productCode) &&
      !_isCIComboMultiPlanGroup(productCode) &&
      !_isHealthComboMultiPlanGroup(productCode)
    )
      return

    const sumAssured = action.payload.value
    const products = yield select(getDisplayProducts)
    const productGroupCodes = _getLifeReadyProductGroupCode(productCode)
    const productGroup = _.filter(products, (product) => _.includes(productGroupCodes, _.get(product, 'code')))
    const product = _.find(productGroup, (product) => _isValidSumAssured(_.get(product, 'entrySumAssured'), sumAssured))
    if (product !== undefined && productCode !== _.get(product, 'code')) {
      yield put(selectBasicPlan(product))
      yield put(editSumAssured(sumAssured))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const onUpdateSelectedLifeReady = function*(action) {
    const sumAssured = yield select(getSumAssured)
    const displayProducts = yield select(getDisplayProducts)
    const insuredOccupationClass = yield select(getInsuredOccupationClass)
    const userAgentType = yield select(getUserAgentType)
    const productSelected = _.find(displayProducts, { code: action.payload })
    const props = { displayProducts, productSelected, insuredOccupationClass, userAgentType }

    if (productSelected) {
      const adjustedProduct = yield call(getAdjustedProductByCampaign, props)
      yield put(selectBasicPlan(adjustedProduct))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const onUpdateSelectedLifeTreasure = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const sumAssured = yield select(getSumAssured)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (!!productSelected) {
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const onUpdateSelectedCancerX5 = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (productSelected) {
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(productSelected.entrySumAssured.minimum))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const onUpdateSelectedKoomverCover = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (productSelected) {
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(productSelected.entrySumAssured.minimum))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  // Koomversoshiled also use this function
  const onUpdateSelectKoomverCompensate = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (productSelected) {
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(productSelected.entrySumAssured.minimum))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  return {
    switchProductBySumAssured,
    onUpdateSelectedLifeReady,
    onUpdateSelectedLifeTreasure,
    onUpdateSelectedCancerX5,
    onUpdateSelectedKoomverCover,
    onUpdateSelectKoomverCompensate,
    getAdjustedProductByCampaign,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* watchers(serviceWrapper) {
  const { LIFE_READY } = serviceWrapper.products

  yield [
    takeLatest([SELECT_PRODUCT_LIFE_READY], LIFE_READY.onUpdateSelectedLifeReady),
    takeLatest([SELECT_PRODUCT_CANCERX5], LIFE_READY.onUpdateSelectedCancerX5),
    takeLatest([SELECT_PRODUCT_KOOMVER_COVER], LIFE_READY.onUpdateSelectedKoomverCover),
    takeLatest([SELECT_PRODUCT_KOOMVER_COMPENSATE], LIFE_READY.onUpdateSelectKoomverCompensate),
    takeLatest([SELECT_PRODUCT_LIFE_TREASURE], LIFE_READY.onUpdateSelectedLifeTreasure),
    takeLatest([UPDATE_SUM_ASSURED$], LIFE_READY.switchProductBySumAssured),
  ]
}

export const sagas = watchers
